'use client'

import { hasStagingBanner } from '@/lib/staging'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

type NavbarOffsetContextValue = {
  navbarOffset: number
  navbarRef: React.RefObject<HTMLElement | null>
}

const NavbarOffsetContext = createContext<NavbarOffsetContextValue | undefined>(
  undefined,
)

type NavbarOffsetProviderProps = {
  children: ReactNode
}

export function NavbarOffsetProvider({ children }: NavbarOffsetProviderProps) {
  const navbarRef = useRef<HTMLElement>(null)
  const [navbarOffset, setNavbarOffset] = useState(64) // Default to 4rem (64px)

  const calculateOffset = useCallback(() => {
    if (!hasStagingBanner()) {
      return
    }

    if (navbarRef.current) {
      const rect = navbarRef.current.getBoundingClientRect()
      setNavbarOffset(rect.bottom)
    }
  }, [])

  useEffect(() => {
    if (!hasStagingBanner()) {
      return
    }

    // Calculate on mount
    calculateOffset()

    // Recalculate on scroll to handle sticky positioning
    window.addEventListener('scroll', calculateOffset)
    window.addEventListener('resize', calculateOffset)

    return () => {
      window.removeEventListener('scroll', calculateOffset)
      window.removeEventListener('resize', calculateOffset)
    }
  }, [calculateOffset])

  return (
    <NavbarOffsetContext.Provider value={{ navbarOffset, navbarRef }}>
      {children}
    </NavbarOffsetContext.Provider>
  )
}

export function useNavbarOffset() {
  const context = useContext(NavbarOffsetContext)

  if (context === undefined) {
    throw new Error('useNavbarOffset must be used within NavbarOffsetProvider')
  }

  return context.navbarOffset
}

export function useNavbarRef() {
  const context = useContext(NavbarOffsetContext)

  if (context === undefined) {
    throw new Error('useNavbarRef must be used within NavbarOffsetProvider')
  }

  return context.navbarRef
}
